import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { findCompanyBySlug } from '../../../client-experience/utils/companyAccess';
import { getPublicAppointmentOrNull } from '../../../api/storefront/storefront';
import '../../styles/LandingPage.css';

const resolvePaymentKind = (pathname, search) => {
  const typeParam = (search.get('type') || '').toLowerCase();
  if (typeParam === 'deposit') return 'deposit';
  if (typeParam === 'balance') return 'balance';
  if (typeParam === 'full') return 'full';

  if (pathname.includes('/deposit/')) return 'deposit';
  if (pathname.includes('/balance/')) return 'balance';
  if (pathname.includes('/account/appointments/')) return 'balance';
  return 'full';
};

/**
 * Storefront-styled payment return for client portal and
 * booking/success routes.
 */
const StorefrontClientPaymentSuccessPage = () => {
  const { companyUrl, appointmentId: appointmentIdParam } = useParams();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [subtitle, setSubtitle] = useState(null);

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const paymentKind = useMemo(
    () => resolvePaymentKind(location.pathname, searchParams),
    [location.pathname, searchParams]
  );

  const appointmentId = appointmentIdParam || null;
  const isAccountFlow = location.pathname.includes('/account/appointments/');

  useEffect(() => {
    let cancelled = false;
    let retryTimer;

    const load = async (attempt = 0) => {
      try {
        if (!appointmentId) {
          throw new Error('No appointment reference found for this payment.');
        }

        const company = await findCompanyBySlug(companyUrl);
        if (!company?.id) {
          throw new Error('Business not found.');
        }

        const loaded = await getPublicAppointmentOrNull({
          companyId: company.id,
          id: appointmentId,
        });

        if (!loaded) {
          if (attempt < 4) {
            retryTimer = setTimeout(() => load(attempt + 1), 2000);
            return;
          }
          throw new Error('Appointment not found yet. Your payment may still be processing.');
        }

        const data = loaded;
        const pub = data?.publicNumber;
        if (!cancelled && pub != null && /^\d+$/.test(String(pub))) {
          setSubtitle(`Appointment #${pub}`);
        }

        if (!cancelled) {
          setLoading(false);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || 'Something went wrong confirming your payment.');
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [appointmentId, companyUrl]);

  const title = loading
    ? 'Confirming your payment…'
    : error
      ? 'Almost there'
      : "You're all set";

  const bodyCopy = (() => {
    if (loading) return <p>Please wait a moment while we confirm your payment.</p>;
    if (error) {
      return (
        <>
          <p>{error}</p>
          <p>
            If you completed payment, the business may still have received it—contact them with
            your email.
          </p>
        </>
      );
    }
    if (paymentKind === 'deposit') {
      return (
        <p>
          Your deposit was received. The remaining balance can be paid from your appointment
          details before or at the time of service.
        </p>
      );
    }
    if (paymentKind === 'balance') {
      return (
        <p>
          Your remaining balance payment was received. You can view the full payment summary in
          your appointment details.
        </p>
      );
    }
    return (
      <p>
        Your payment was received. Your appointment details will appear in your confirmation
        email shortly. If anything looks wrong, contact the business directly.
      </p>
    );
  })();

  const viewAppointmentHref =
    appointmentId && companyUrl
      ? `/book/${companyUrl}/account/appointments/${appointmentId}`
      : null;

  return (
    <div className="landing-page">
      <div className="container storefront-booking-success">
        <div className="storefront-booking-success__card">
          <h1>{title}</h1>
          {!loading && !error && subtitle ? (
            <p className="storefront-booking-success__ref">{subtitle}</p>
          ) : null}
          {bodyCopy}
          {!loading ? (
            <div className="storefront-booking-success__actions">
              {isAccountFlow && viewAppointmentHref ? (
                <Link to={viewAppointmentHref} className="back-link">
                  View appointment
                </Link>
              ) : null}
              <Link to={companyUrl ? `/book/${companyUrl}/account` : '/'} className="back-link">
                {isAccountFlow ? 'Return to home' : 'Back to booking site'}
              </Link>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default StorefrontClientPaymentSuccessPage;
